/*
  IG predictive search behaviour.
  Loaded from layout/theme.liquid when settings.use_ig_search is true.

  Responsibilities:
    - Toggle `search--ig--has-query` on the modal to swap between the
      "Te puede interesar" / mobile body view and the live results view.
    - Show/hide the desktop clear (X) button.
    - Wire desktop clear, mobile clear ("Borrar") and mobile close X.
    - Delegate clicks on suggestion chips + popular pills into the search
      input so the base predictive-search re-runs its fetch.
    - Tab switching (desktop): sync active tag, swap featured collection
      panel, and hide results whose `data-product-tags` don't include the
      active tag. Skipped on mobile (no tab UI).
    - Wire the mobile search icon (`#search-icon-open-mobile`) which the
      base SearchModal doesn't bind.
*/
(function () {
  'use strict';

  const MODAL_SELECTOR = 'search-modal.search--ig';
  const HAS_QUERY_CLASS = 'search--ig--has-query';

  function init(modal) {
    if (!modal || modal.dataset.igInit === '1') return;
    modal.dataset.igInit = '1';

    const input = modal.querySelector('.search--ig__input');
    const clearBtn = modal.querySelector('[data-ig-clear]');
    const mobileClearBtn = modal.querySelector('[data-ig-mobile-clear]');
    const mobileCloseBtn = modal.querySelector('[data-ig-mobile-close]');
    const resultsHost = modal.querySelector('[data-predictive-search]');
    const predictiveEl = modal.querySelector('predictive-search');
    const tabs = Array.from(modal.querySelectorAll('[data-ig-tab]'));
    const featuredPanels = Array.from(modal.querySelectorAll('[data-ig-tab-panel]'));

    if (!input) return;

    let activeTag = '';
    if (tabs[0]) {
      activeTag = (tabs[0].dataset.igTabTag || '').trim().toLowerCase();
    }

    const syncQueryState = () => {
      const hasQuery = input.value.trim().length > 0;
      modal.classList.toggle(HAS_QUERY_CLASS, hasQuery);
      if (clearBtn) {
        clearBtn.hidden = !hasQuery;
      }
      // Mobile clear button and the "Ver todos" link are driven by CSS via
      // the `.search--ig--has-query` class — nothing to toggle here.
    };

    const resetQuery = () => {
      input.value = '';
      syncQueryState();
      const slot = modal.querySelector('[data-ig-chips-slot]');
      if (slot) slot.replaceChildren();
      if (predictiveEl && typeof predictiveEl.close === 'function') {
        predictiveEl.close(true);
      }
      input.focus();
    };

    const applyFeaturedPanelFilter = () => {
      featuredPanels.forEach((panel) => {
        const panelTag = (panel.dataset.igTabPanel || '').trim().toLowerCase();
        panel.hidden = panelTag !== activeTag;
      });
    };

    const mobileQuery = window.matchMedia('(max-width: 989px)');

    const applyResultsTagFilter = () => {
      // Filter cards inside the live predictive-search results by data-product-tags.
      // The tabs render on mobile too (Figma 17134:47966), so the filter applies
      // at every breakpoint. When activeTag is empty, show everything.
      const resultCards = modal.querySelectorAll(
        '[data-ig-results] [data-product-tags]'
      );
      if (!resultCards.length) return;
      const skipFilter = !activeTag;
      resultCards.forEach((card) => {
        if (skipFilter) {
          card.hidden = false;
          return;
        }
        const tagList = (card.dataset.productTags || '')
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean);
        card.hidden = !tagList.includes(activeTag);
      });
    };

    const setActiveTab = (tab) => {
      if (!tab) return;
      activeTag = (tab.dataset.igTabTag || '').trim().toLowerCase();
      tabs.forEach((t) => {
        const isActive = t === tab;
        t.classList.toggle('search--ig__tab--active', isActive);
        t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      applyFeaturedPanelFilter();
      applyResultsTagFilter();
    };

    /* The results panel is absolutely positioned on mobile (it lives inside the
       form for the desktop layout), so it needs to know where the input + tabs
       block ends. Measuring beats hard-coding: a wrapped placeholder or a
       different locale would otherwise make it overlap the tabs. */
    const inputRow = modal.querySelector('.search--ig__row--input');
    const content = modal.querySelector('.search--ig__content');

    const syncResultsOffset = () => {
      if (!inputRow || !content || !mobileQuery.matches) return;
      // Measure against whatever ends up being the panel's containing block —
      // `predictive-search` is position: relative, so it, not the content
      // wrapper, is what `top` resolves against.
      const anchor = (resultsHost && resultsHost.offsetParent) || content;
      const offset = inputRow.getBoundingClientRect().bottom
        - anchor.getBoundingClientRect().top;
      if (offset > 0) {
        content.style.setProperty('--ig-search-results-top', offset + 'px');
      }
    };

    // Initial state (in case the input already has a value from the URL)
    syncQueryState();
    syncResultsOffset();
    window.addEventListener('resize', syncResultsOffset);

    input.addEventListener('input', syncQueryState);

    if (clearBtn) {
      clearBtn.addEventListener('click', (event) => {
        event.preventDefault();
        resetQuery();
      });
    }

    if (mobileClearBtn) {
      mobileClearBtn.addEventListener('click', (event) => {
        event.preventDefault();
        resetQuery();
      });
    }

    if (mobileCloseBtn) {
      mobileCloseBtn.addEventListener('click', (event) => {
        event.preventDefault();
        if (typeof modal.close === 'function') {
          modal.close();
        } else {
          modal.classList.remove('active');
        }
      });
    }

    // Wire the mobile search icon in the header (Madrid's SearchModal only
    // binds `#search-icon-open`, which is the desktop trigger).
    const mobileTrigger = document.getElementById('search-icon-open-mobile');
    if (mobileTrigger && !mobileTrigger.dataset.igWired) {
      mobileTrigger.dataset.igWired = '1';
      mobileTrigger.setAttribute('role', 'button');
      mobileTrigger.setAttribute('aria-haspopup', 'dialog');
      mobileTrigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (typeof modal.open === 'function') {
          modal.open(mobileTrigger);
        } else {
          modal.classList.add('animate', 'active');
        }
      });
    }

    // Suggestion chip delegation (chips are re-rendered on each fetch)
    modal.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-ig-suggestion-chip]');
      if (!chip) return;
      event.preventDefault();
      const q = chip.dataset.q || chip.textContent.trim();
      input.value = q;
      syncQueryState();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    });

    // Tab click: switch active tab, swap featured collection panel,
    // and re-apply the tag filter to any live results.
    tabs.forEach((tab) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        setActiveTab(tab);
      });
    });

    /* Mobile parks the suggestion row under the input, above the tabs
       (Figma 17134:48118); desktop keeps it at the top of the results panel.
       The row is re-rendered on every fetch, so this runs from the same
       observer that re-applies the tag filter. */
    const chipsSlot = modal.querySelector('[data-ig-chips-slot]');

    const syncChipsPlacement = () => {
      if (!chipsSlot) return;
      const chips = modal.querySelector('[data-ig-chips]');
      if (!chips) {
        chipsSlot.replaceChildren();
        return;
      }
      if (mobileQuery.matches) {
        if (chips.parentElement !== chipsSlot) chipsSlot.appendChild(chips);
      } else if (chips.parentElement === chipsSlot) {
        const panel = modal.querySelector('.predictive-search__ig');
        if (panel) panel.prepend(chips);
      }
      syncResultsOffset();
    };

    // Re-apply the tag filter each time new results are injected
    if (resultsHost) {
      const resultsObserver = new MutationObserver(() => {
        applyResultsTagFilter();
        syncChipsPlacement();
      });
      resultsObserver.observe(resultsHost, {
        childList: true,
        subtree: true,
      });
    }

    // Re-apply on viewport transitions (mobile <-> desktop)
    const onBreakpointChange = () => {
      applyResultsTagFilter();
      syncChipsPlacement();
    };
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', onBreakpointChange);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(onBreakpointChange);
    }

    // Initial state
    applyFeaturedPanelFilter();

    // When the modal opens (SearchModal adds `.active`), refocus the input
    // so users can type immediately.
    const focusObserver = new MutationObserver(() => {
      if (modal.classList.contains('active')) {
        // Small delay so the transition completes before focusing
        window.requestAnimationFrame(() => {
          syncResultsOffset();
          input.focus();
        });
      }
    });
    focusObserver.observe(modal, { attributes: true, attributeFilter: ['class'] });

  }

  function boot() {
    document.querySelectorAll(MODAL_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
